import type { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { type SseFrame, readSseFrames } from '@/lib/streaming/sse';

export type DealItemsBatch = {
  items: DealItemWithVersions[];
  isLastBatch: boolean;
};

export class DealItemsStreamError extends Error {
  readonly status?: number;
  readonly body?: string;

  constructor(message: string, status?: number, body?: string) {
    super(message);
    this.name = 'DealItemsStreamError';
    this.status = status;
    this.body = body || undefined;
  }
}

type StreamDealItemsOptions = {
  baseUrl: string;
  dealId: string;
  token: string;
  signal: AbortSignal;
  onBatch: (batch: DealItemsBatch) => void;
};

export async function streamDealItems({
  baseUrl,
  dealId,
  token,
  signal,
  onBatch,
}: StreamDealItemsOptions): Promise<void> {
  const response = await fetch(`${baseUrl}/deals/${dealId}/items/stream`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'text/event-stream',
    },
    signal,
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new DealItemsStreamError(
      `Deal items stream failed with status ${response.status}`,
      response.status,
      body
    );
  }
  if (!response.body) {
    throw new DealItemsStreamError('Deal items stream returned an empty body');
  }

  let sawLastBatch = false;

  const handleFrame = (frame: SseFrame): void => {
    if (frame.event === 'error') {
      throw new DealItemsStreamError(
        'Deal items stream emitted an error event',
        undefined,
        frame.data
      );
    }
    if (!frame.data) return;
    let batch: DealItemsBatch;
    try {
      batch = JSON.parse(frame.data) as DealItemsBatch;
    } catch {
      throw new DealItemsStreamError(
        'Deal items stream sent a malformed data frame'
      );
    }
    if (batch.isLastBatch) sawLastBatch = true;
    onBatch(batch);
  };

  try {
    await readSseFrames(response.body, handleFrame);
    if (!signal.aborted && !sawLastBatch) {
      throw new DealItemsStreamError(
        'Deal items stream ended before the last batch'
      );
    }
  } catch (error) {
    if (signal.aborted) return;
    throw error;
  }
}
